<template>
  <v-container class="py-8 bg-light-blue-main">
    <v-btn
      prepend-icon="mdi-arrow-left"
      @click="router.back()"
      >Zurück</v-btn
    >
    <h1>{{ $t("views.organization.edit") }}</h1>

    <v-card>
      <v-card-text>
        <v-form>
          <v-text-field
            data-cy="organization-name"
            class="mb-4 mt-2"
            v-model="name"
            :rules="[required]"
            :disabled="!allowedEdit"
            label="Name" />
          <v-text-field
            data-cy="organization-description"
            v-model="description"
            :disabled="!allowedEdit"
            label="Description" />
        </v-form>
      </v-card-text>
    </v-card>
    <template v-if="current_status !== 'paid'">
      <v-alert
        data-cy="licence-not-paid"
        color="error"
        :dismissible="false">
        <span>{{ $t("views.licence.other.not-paid") }}</span>
        <v-spacer></v-spacer>
        <v-btn
          style="color: black"
          data-cy="licence-pay-now"
          v-if="current_status !== 'draft'"
          @click="payNow">
          {{ $t("views.licence.other.pay-now") }}
        </v-btn>
      </v-alert>
    </template>
    <div :class="current_status !== 'paid' ? 'disabled-change-module' : ''">
      <v-card>
        <v-card-text>
          <v-data-table
            :headers="userListHeaders"
            :items="userListItems">
            <template v-slot:header.actions="{}">
              <v-btn
                v-if="allowedEdit"
                data-cy="add-user"
                @click="addUser()"
                ><v-icon>mdi-plus</v-icon></v-btn
              >
            </template>
            <template
              v-if="allowedEdit"
              v-slot:item.actions="{ item, index }">
              <v-btn
                v-if="item.state !== 2"
                :data-cy="`edit-user-${item.email.replaceAll(/[^\w]/g, '')}`"
                @click="editUser(item, index)"
                ><v-icon>mdi-pencil</v-icon></v-btn
              >
              <v-btn
                :data-cy="`remove-user-${item.email}`"
                v-if="item.rights !== 1"
                @click="removeUser(index)"
                ><v-icon>mdi-delete</v-icon></v-btn
              >
            </template>
          </v-data-table>
          <v-dialog
            v-model="userDialog.dialog"
            persistent>
            <v-card
              width="60%"
              class="mx-auto">
              <v-card-title data-cy="dialog-user-title">
                {{ $t("views.organization.editUser") }}
              </v-card-title>
              <v-card-text>
                <v-form ref="userForm">
                  <v-text-field
                    class="mt-2"
                    data-cy="dialog-user-email"
                    :disabled="userDialog.rights === 1"
                    v-model="userDialog.email"
                    :rules="emailRules"
                    label="Email" />
                  <v-select
                    data-cy="dialog-user-rights"
                    class="mt-4"
                    :disabled="userDialog.rights === 1"
                    v-model="userDialog.rights"
                    :items="
                      userRights.filter(
                        (option) =>
                          userDialog.rights === 1 || option.value !== 1
                      )
                    ">
                    <template v-slot:item="{ props: itemProps, item }">
                      <v-list-item v-bind="itemProps">
                        <template v-slot:title>
                          {{ item.title }}
                          {{ itemProps.extra }}
                        </template>
                      </v-list-item>
                    </template>
                  </v-select>
                  <LicenceModuleSelectElement
                    data-cy="dialog-user-modules"
                    label="Lizenz"
                    class="mt-4"
                    v-model="userDialog.modules"
                    :options="moduleSelectOptions"></LicenceModuleSelectElement>
                </v-form>
              </v-card-text>
              <v-card-actions>
                <v-btn
                  data-cy="dialog-user-save"
                  @click="userDialog.callback()"
                  >{{ $t("views.organization.save") }}</v-btn
                >
                <v-btn
                  data-cy="dialog-user-cancel"
                  @click="userDialog.dialog = false"
                  >{{ $t("views.organization.cancel") }}</v-btn
                >
              </v-card-actions>
            </v-card>
          </v-dialog>
        </v-card-text>
      </v-card>

      <v-alert
        v-if="pendingSwitch"
        type="info"
        data-cy="licence-pending-mode-switch">
        {{
          $t(
            "views.licence.other.pendingSwitchInfo" +
              (next_phase_mode === 'yearly' ? 'Yearly' : 'Monthly'),
            {
              date: pendingDate,
              currentCosts: pendingCosts.current,
              newCosts: pendingCosts.next
            }
          )
        }}
      </v-alert>
      <PromoBox
        v-if="loaded && allowedEdit"
        :promo="promo"
        :trials="trials"
        :active-discount="activeDiscount"
        show-book
        :booked="trialBooked"
        @book="bookTrial" />
      <v-card v-if="allowedEdit">
        <ModuleListSlots
          v-if="loaded"
          ref="moduleList"
          :promo-code="promo.appliedCode.value"
          :promo-info="promo.promoInfo.value"
          :active-discount="activeDiscount"
          :slots="bookedSlots"
          for-organization
          :free-save="freeSave"
          :current_phase="current_phase"
          :current_phase_mode="current_phase_mode"
          :next_phase="next_phase"
          :next_phase_mode="next_phase_mode"
          :current_status="current_status"
          :licence_id="licenceId"
          :next_billing_cycle_anchor="next_billing_cycle_anchor"
          v-model:mode="paymentMode"></ModuleListSlots>
      </v-card>
      <v-card v-if="allowedEdit">
          <div v-if="moduleListRef?.someoneLeft" style="background-color: darkred; color: white; padding: 10px">
              <span>{{ $t("views.organization.someoneLeft") }}</span>
          </div>
        <v-card-actions>
          <DeleteWithConfirmationButton
            :deleteAction="
              () => mainStore.organizationDelete(Number(organizationId))
            " />

          <v-spacer></v-spacer>
          <v-btn @click="router.back()">Abbrechen</v-btn>
          <v-btn
            color="success"
            data-cy="save-organization"
            @click="save"
            >Speichern</v-btn
          >
        </v-card-actions>
      </v-card>
    </div>
  </v-container>
</template>

<script setup lang="ts">
import { computed, onMounted, Ref, ref, useTemplateRef } from "vue";
import { useUserRules } from "@/validations/user";
import { useCommonRules } from "@/validations/common";
import { useTranslation } from "i18next-vue";
import { useMainStore } from "@/stores/mainStore";
import { useAuthStore } from "@/stores/authStore";
import axios from "axios";
import {
  ORGANIZATION_RIGHT_COOWNER,
  ORGANIZATION_RIGHT_OWNER,
  ORGANIZATION_RIGHT_READ,
  organizationRightNames
} from "@/utils/constants";
import LicenceModuleSelectElement from "@/components/Elements/LicenceModuleSelectElement.vue";
import { useAxiosStore } from "@/stores/axiosStore";
import { useRoute, useRouter } from "vue-router";
import { useConfirm, useSnackbar } from "vuetify-use-dialog";
import ModuleListSlots from "../Licence/ModuleListSlots.vue";
import {
  getPhaseCosts,
  hasRealBooking,
  usePromo
} from "../Licence/moduleComposable";
import PromoBox from "../Licence/PromoBox.vue";
import DeleteWithConfirmationButton from "@/components/tools/DeleteWithConfirmationButton.vue";

const createConfirm = useConfirm();
const createSnackbar = useSnackbar();
const route = useRoute();
const router = useRouter();

const allowedEdit = computed(() => {
  return (
    mainStore.organizationRights(route.params.id as string) &
    (ORGANIZATION_RIGHT_OWNER | ORGANIZATION_RIGHT_COOWNER)
  );
});

type User = {
  email: string;
  rights: number;
  modules: number;
  newModules: number;
  state: number;
  pending?: number;
};

const organizationId = computed(() => route.params.id);

const mainStore = useMainStore();
const authStore = useAuthStore();
const axiosStore = useAxiosStore();

const { t } = useTranslation();

const { emailRules } = useUserRules(t);
const { required } = useCommonRules(t);

const licenceId = ref(0);
const current_status = ref("paid");

// Active module trials (access only) and the price discount of the licence.
// Members can be assigned booked + trial slots, but only booked slots are sent
// for booking and billing (bookedSlots).
const trials = ref<{ module: string; quantity: number; end_date: string }[]>([]);
const trialPhase = ref<Record<string, number>>({});
// no subscription but module trials: saved without Stripe until slots are booked
const trialPath = ref(false);
const activeDiscount = ref<any>(null);
// trial slots the user chose to book permanently (sent as takeover; slots booked
// beyond the trial leave it unchanged)
const trialBooked = ref<Record<string, number>>({});

const promo = usePromo(licenceId, () => loadOrganization());

const moduleListRef = useTemplateRef("moduleList");

onMounted(() => {
  loadOrganization();
});

const loaded = ref(false);

const moduleSelectOptions = computed(() => {
  return Object.values(mainStore.modules)
    .map((module) => ({
      title: t("views.licence.modules." + module.name + ".name"),
      value: module.value,
      props: {
        "data-cy": `select-option-${module.name}`,
        requires: module.requires,
        valueName: module.name,
        disabled: module.disabled
      }
    }))
    .filter((option) => Number(option.value) !== 32 && !option.props.disabled);
});

function formatModules(
  modules: Record<string, number>
): Record<number, number> {
  console.log("Hier", modules);
  return Object.fromEntries(
    mainStore.modules.map((module) => [module.value, modules[module.name]])
  );
}

async function loadOrganization() {
  const {
    data: { organization, licence }
  } = await axios.get("/api/organization/" + organizationId.value);

  name.value = organization.name;
  description.value = organization.description;

  // members without owner/co-owner rights only get the effective slots, no licence data
  const fullLicence = !!licence.current_phase;
  if (fullLicence) {
    current_phase.value = formatModules(licence.current_phase);
    current_phase_mode.value = licence.current_phase_mode;
    next_phase.value = formatModules(licence.next_phase);
    next_billing_cycle_anchor.value = licence.next_billing_cycle;
    next_phase_mode.value = licence.next_phase_mode;
    paymentMode.value = licence.next_phase_mode;
  }

  console.log(licence, organization);

  for (const user of organization.organization_user) {
    userListItems.value.push({
      email: user.users.email,
      rights: user.rights,
      modules: Number(user.licence_modules),
      // members do not get licence_modules_new, they see the current modules
      newModules: Number(user.licence_modules_new ?? user.licence_modules),
      state: user.state
    });
  }

  for (const user of organization.pending_invites) {
    userListItems.value.push({
      email: user.email,
      rights: user.rights,
      modules: Number(user.licence_modules),
      newModules: Number(user.licence_modules),
      state: 0,
      pending: user.id
    });
  }

  if (fullLicence) {
    licenceId.value = licence.id;
    trialPath.value = !!licence.trial_path;
    // only module trials in use, nothing is booked: nothing to pay
    current_status.value =
      licence.licence?.payment_status ??
      (trialPath.value && !hasRealBooking(next_phase.value) ? "paid" : null);
    trials.value = licence.trials ?? [];
    trialPhase.value = licence.trial_phase ?? {};
    activeDiscount.value = licence.promotion ?? null;
    trialBooked.value = {};
  }

  loaded.value = true;
}

const allModules = computed(() => {
  return userListItems.value.reduce(
    (acc, user) => {
      for (const module of mainStore.modules) {
        acc[module.value] = acc[module.value] || 0;
        if (current_status.value !== "paid") {
          acc[module.value] += user.newModules & module.value ? 1 : 0;
        } else {
          acc[module.value] += user.modules & module.value ? 1 : 0;
        }
      }
      return acc;
    },
    { 32: 1 }
  );
});

// assigned slots minus the trial slots that are not booked
const bookedSlots = computed(() => {
  const slots = Object.fromEntries(
    Object.entries(allModules.value).map(([key, assigned]) => {
      const name = mainStore.modules.find(
        (module) => Number(module.value) === Number(key)
      )?.name;
      const freeTrial = Math.max(
        0,
        (trialPhase.value[name as string] ?? 0) -
          (trialBooked.value[name as string] ?? 0)
      );
      return [key, Math.max(0, assigned - freeTrial)];
    })
  );
  // the mandatory private_spaces slot is charged with the first real booking
  return trialPath.value && !hasRealBooking(slots) ? { ...slots, 32: 0 } : slots;
});

const freeSave = computed(
  () => trialPath.value && !hasRealBooking(bookedSlots.value)
);

function bookTrial(module: string, quantity: number) {
  // never book more than the trial has slots
  trialBooked.value[module] = Math.min(
    (trialBooked.value[module] ?? 0) + quantity,
    trialPhase.value[module] ?? 0
  );
}

// the server answers 400 {error, data} for promo and slot problems; shown once
let saveErrorShown = false;
function onSaveError(error: any) {
  if (saveErrorShown) return;
  if (promo.showApiError(error)) saveErrorShown = true;
}

const name = ref("");
const description = ref("");
const mode = ref(0);
const modules = ref({});
const current_phase = ref({});
const current_phase_mode = ref("monthly");
const next_phase = ref({});
const next_billing_cycle_anchor = ref<null | number>(null);
const next_phase_mode = ref("monthly");
const paymentMode = ref("monthly");

const pendingSwitch = computed(
  () =>
    !!next_billing_cycle_anchor.value &&
    current_phase_mode.value !== next_phase_mode.value
);
const pendingDate = computed(() =>
  new Date((next_billing_cycle_anchor.value ?? 0) * 1000).toLocaleDateString()
);
const pendingCosts = computed(() => ({
  current: getPhaseCosts(
    current_phase.value,
    current_phase_mode.value,
    mainStore.modules
  ),
  next: getPhaseCosts(next_phase.value, next_phase_mode.value, mainStore.modules)
}));
const userListItems: Ref<User[]> = ref([]);

const userRights = Object.entries(organizationRightNames).map(
  ([key, value]) => ({
    title: t("other.rights." + value),
    value: Number(key),
    props: {
      "data-cy": `select-option-${value}`,
      extra: t("other.rights." + value + "-extra")
    }
  })
);

const userListHeaders = [
  { title: t("views.organization.userList.email"), value: "email" },
  {
    title: t("views.organization.userList.rights"),
    key: "rights",
    value: (item) =>
      userRights
        .filter((option) => option.value & item.rights)
        .map((option) => option.title)
        .join(", ")
  },
  {
    title: t("views.organization.userList.licence"),
    key: "modules",
    value: (item) =>
      moduleSelectOptions.value
        .filter((option) =>
          current_status.value !== "paid"
            ? option.value & item.newModules
            : option.value & item.modules
        )
        .map((option) => option.title)
        .join(", ") || "Keine",
    width: 300
  },
  {
    title: "",
    key: "state",
    value: (item) => {
      console.log("state", item.state);
      return stateOptions[item.state];
    }
  },
  { title: "actions", value: "actions" }
];

const stateOptions = {
  0: t("other.state.inactive"),
  1: "", //t("other.state.active"),
  2: t("other.state.declined")
};

const userFormRef = useTemplateRef("userForm");

const userDialog = ref({
  dialog: false,
  email: "",
  rights: ORGANIZATION_RIGHT_READ,
  modules: 0,
  index: -1,
  callback: () => {}
});

const userListEmails = computed(() => {
  return userListItems.value.map((item) => item.email);
});

function addUser() {
  userDialog.value = {
    dialog: true,
    email: "",
    rights: ORGANIZATION_RIGHT_READ,
    modules: 0,
    index: -1,
    callback: () => {
      userFormRef.value.validate().then((valid) => {
        if (valid.valid) {
          if (
            userDialog.value.email &&
            !userListEmails.value.includes(userDialog.value.email)
          ) {
            userListItems.value.push({
              email: userDialog.value.email,
              rights: userDialog.value.rights,
              modules: userDialog.value.modules,
              newModules: userDialog.value.modules,
              state: 0
            });
            userDialog.value.dialog = false;
          }
        }
      });
    }
  };
}

function editUser(item: User, index: number) {
  userDialog.value = {
    dialog: true,
    email: item.email,
    rights: item.rights,
    modules: item.modules,
    index: index,
    callback: () => {
      const user = userListItems.value.splice(index, 1)[0];
      userListItems.value.splice(index, 0, {
        email: userDialog.value.email,
        rights: userDialog.value.rights,
        modules: userDialog.value.modules,
        newModules: userDialog.value.modules,
        state: user.state,
        pending: user.pending
      });
      userDialog.value.dialog = false;
    }
  };
}

function removeUser(index: number) {
  userListItems.value.splice(index, 1);
}

function saveChanges() {
  console.log("saveChanges");

  if (!moduleListRef.value) {
    axiosStore.execute({
      method: "POST",
      url: "/api/organization/edit/" + organizationId.value,
      data: {
        name: name.value,
        description: description.value,
        modules: modules.value,
        users: userListItems.value,
        licenceId: licenceId.value
      }
    });
    return;
  }

  const { monthlyCost, additionalCharge, modulesRemoved } =
    moduleListRef.value.getInfos();

  const sendModules = computed(() => {
    return Object.fromEntries(
      Object.entries(bookedSlots.value).map(([key, value]) => [
        mainStore.modules.find((module) => Number(module.value) === Number(key))
          .name,
        value
      ])
    );
  });

  // payment for remaining days of the month
  //
  createConfirm({
    dialogProps: { width: 400 },
    cardProps: { color: "warning" },
    title: t(`actions.confirmation.change-licence.title`),
    content:
      t(`actions.confirmation.change-licence.content`, {
        newPrice: monthlyCost
      }) +
      (additionalCharge
        ? t(
            `actions.confirmation.change-licence.module-added`,
            additionalCharge
          )
        : "") +
      (modulesRemoved.length
        ? t(`actions.confirmation.change-licence.module-removed`, {
            removedModules: modulesRemoved
              .map((module) => module.module)
              .join(", ")
          })
        : ""),
    cardTextProps: { style: { "white-space": "pre-wrap" } },
    confirmationText: t(`actions.confirmation.change-licence.confirm`),
    cancellationText: t(`actions.confirmation.change-licence.cancel`)
    // confirmationButtonProps: { color: "warning" }
  }).then((confirm) => {
    if (confirm) {
      axiosStore.execute(
        {
          method: "POST",
          url: "/api/organization/edit/" + organizationId.value,
          data: {
            name: name.value,
            description: description.value,
            modules: sendModules.value,
            users: userListItems.value,
            licenceId: licenceId.value,
            promoCode: promo.appliedCode.value
          }
        },
        () => {
          router.push({ name: "profile" });
        }
      );

      createSnackbar({
        text: t("actions.saved"),
        showCloseButton: false,
        snackbarProps: { color: "error", timeout: 1000 }
      });
    }
  });
}

function save() {
  if (!moduleListRef.value) {
    console.log("Error");
    return;
  }
  saveErrorShown = false;
  const sendModules = computed(() => {
    return Object.fromEntries(
      Object.entries(bookedSlots.value).map(([key, value]) => [
        mainStore.modules.find((module) => Number(module.value) === Number(key))
          .name,
        value
      ])
    );
  });

  moduleListRef.value.saveModules(() => {
    axiosStore.execute(
      {
        method: "POST",
        url: "/api/organization/edit/" + organizationId.value,
        data: {
          name: name.value,
          description: description.value,
          modules: sendModules.value,
          users: userListItems.value,
          licenceId: licenceId.value,
          promoCode: promo.appliedCode.value,
          paymentMode: paymentMode.value,
          // trial slots taken over by booking them: only these convert the trial
          takeover: trialBooked.value,
          inviterName: authStore.user.first_name + " " + authStore.user.last_name
        }
      },
      (response) => {
        router.push(response.data || { name: "profile" });

        // mainStore.loadOrganizations();

        // router.push({ name: "profile" });
      },
      onSaveError
    );
  });
}

function payNow() {
  if (!current_status.value) {
    save();
  } else {
    axios
      .get("/api/stripe/latest-invoice/" + licenceId.value)
      .then((response) => {
        router.push({
          name: "payment-invoice",
          params: { licence_id: licenceId.value, invoice_id: response.data.id }
        });
      })
      .catch((err) => {
        console.error(err);
        createSnackbar({
          text: t("actions.error-message"),
          showCloseButton: false,
          snackbarProps: { color: "error", timeout: 2000 }
        });
      });
  }
}
</script>
<style>
.disabled-change-module {
  opacity: 0.7;
  pointer-events: none;
}
</style>
